import { DxfInput, DxfImageInput, PlaceFaceInput } from '../generated/graphql';
import {
  RejectReason,
  RejectedFile,
  buildAcceptAttribute,
  getUploadFileExtension,
  isAllowedUploadExtension,
  isValidUploadSize,
} from '../utils/FileUpload.logic';
import { ExistingImage } from '../utils/AttachedFileList.logic';

export const DRAWING_ONLY_EXTENSIONS = ['dxf', 'pdf'];
export const DRAWING_ZONE_ACCEPT = buildAcceptAttribute('face-drawing');
export const IMAGE_ZONE_ACCEPT = buildAcceptAttribute('face-image');

export interface DroppedFileClassification {
  drawing: File | null;
  images: File[];
  rejected: RejectedFile[];
}

export interface BuildPlaceFaceInputParams {
  placeId: number;
  existingId: number | undefined;
  existingRevision: number | null | undefined;
  dxf: DxfInput | undefined;
  dxfImages: DxfImageInput[];
  keptExistingImages: ExistingImage[];
  scaleEditable: boolean;
  scale: number | null;
  pictureSymbolPrefix: string;
}

type FileCategory = 'unsupported' | 'too_large' | 'drawing_only' | 'raster';

export function isRasterImageFile(fileName: string): boolean {
  return isAllowedUploadExtension('face-image', fileName);
}

export function isDrawingOnlyFile(fileName: string): boolean {
  return DRAWING_ONLY_EXTENSIONS.includes(getUploadFileExtension(fileName) ?? '');
}

export function isDxfFile(fileName: string): boolean {
  return getUploadFileExtension(fileName) === 'dxf';
}

// 拡張子と容量だけでファイルの区分を決める
function toFileCategory(file: File): FileCategory {
  const drawingOnly = isDrawingOnlyFile(file.name);
  const raster = isRasterImageFile(file.name);
  if (!drawingOnly && !raster) {
    return 'unsupported';
  }
  if (!isValidUploadSize(file.size)) {
    return 'too_large';
  }
  if (drawingOnly) {
    return 'drawing_only';
  }
  return 'raster';
}

// 図面として採用する位置を決める。採用できるものが無ければ -1
function decideDrawingIndex(
  categories: FileCategory[],
  validDrawingCount: number,
  drawingAlreadyAttached: boolean
): number {
  if (drawingAlreadyAttached) {
    return -1;
  }
  if (validDrawingCount >= 2) {
    return -1;
  }
  if (validDrawingCount === 1) {
    return categories.indexOf('drawing_only');
  }
  return categories.indexOf('raster');
}

// 図面に採用しなかった有効ファイルの拒否理由。null なら関連画像として採用する
function decideValidFileRejectReason(
  category: FileCategory,
  drawingAlreadyAttached: boolean,
  validDrawingCount: number,
  drawing: File | null
): RejectReason | null {
  if (drawingAlreadyAttached) {
    if (category === 'drawing_only') {
      return 'drawing_already_attached';
    }
    return null;
  }
  if (validDrawingCount >= 2) {
    return 'extra_drawing';
  }
  if (drawing !== null && isDxfFile(drawing.name)) {
    return null;
  }
  return 'images_require_dxf';
}

function pickDrawing(files: File[], drawingIndex: number): File | null {
  if (drawingIndex >= 0) {
    return files[drawingIndex];
  }
  return null;
}

export function classifyDroppedFiles(
  files: File[],
  drawingAlreadyAttached: boolean
): DroppedFileClassification {
  const categories = files.map(toFileCategory);
  const validDrawingCount = categories.filter((category) => category === 'drawing_only').length;
  const drawingIndex = decideDrawingIndex(categories, validDrawingCount, drawingAlreadyAttached);
  const drawing = pickDrawing(files, drawingIndex);
  const images: File[] = [];
  const rejected: RejectedFile[] = [];
  for (let i = 0; i < files.length; i++) {
    const category = categories[i];
    if (category === 'unsupported' || category === 'too_large') {
      rejected.push({ file: files[i], reason: category });
    } else if (i !== drawingIndex) {
      const reason = decideValidFileRejectReason(
        category,
        drawingAlreadyAttached,
        validDrawingCount,
        drawing
      );
      if (reason === null) {
        images.push(files[i]);
      } else {
        rejected.push({ file: files[i], reason });
      }
    }
  }
  return { drawing, images, rejected };
}

export function buildPlaceFaceInput(params: BuildPlaceFaceInputParams): PlaceFaceInput {
  const input: PlaceFaceInput = {
    place_id: params.placeId,
    dxf_images: params.dxfImages,
    uploaded_dxf_images: params.keptExistingImages.map((image) => image.id),
    picture_symbol_prefix: params.pictureSymbolPrefix,
  };
  if (params.existingId !== undefined) {
    if (params.existingRevision === undefined || params.existingRevision === null) {
      throw new Error('編集対象のリビジョンが取得できませんでした。');
    }
    input.id = params.existingId;
    input.revision = params.existingRevision + 1;
  }
  if (params.dxf !== undefined) {
    input.dxf = params.dxf;
  }
  if (params.scaleEditable) {
    input.scale = params.scale ?? 1;
  }
  return input;
}

export function getAttachedDrawingName(
  dxfFile: File | null,
  existingDrawingName: string | null
): string {
  if (dxfFile !== null) {
    return dxfFile.name;
  }
  if (existingDrawingName !== null) {
    return existingDrawingName;
  }
  return '';
}

// ほかのタブの登録・編集画面のボタンと同じく、新規は「登録」、編集は「保存」にそろえる
export function getSubmitLabel(existingId: number | undefined): string {
  if (existingId !== undefined) {
    return '保存';
  }
  return '登録';
}

// ほかのタブの登録・編集画面と同じく、段落の上の小見出しで新規と編集のどちらの画面かを示す
export function getPageTitle(existingId: number | undefined): string {
  if (existingId !== undefined) {
    return '図面の編集';
  }
  return '図面の登録';
}

// 保存に成功したときに図面一覧の上へ出す通知の文言
export function getSaveSuccessMessage(existingId: number | undefined): string {
  if (existingId !== undefined) {
    return '図面を保存しました';
  }
  return '図面を登録しました';
}

// 保存に失敗したときに出す通知の文言
export function getSaveFailureMessage(existingId: number | undefined): string {
  if (existingId !== undefined) {
    return '図面の保存に失敗しました';
  }
  return '図面の登録に失敗しました';
}

